import { APIScopeData } from '@ardoq/api-types';
import { logError } from '@ardoq/sentry';
import { GridApi } from 'ag-grid-enterprise';

import { scopeDataOperations } from '@ardoq/scope-data';
import type { SharedInventoryContext } from './types';

// Logs if scope data does not have dateRangeFieldMap which is an indication of date ranges not being merged.
const logIfScopeDataDateRangeIsNotMerged = (
  scopeDataList: Array<{
    scopeData: APIScopeData;
    name: string;
  }>
): void => {
  const createError = (name: string): Error =>
    new Error(
      `Date range field map is not created for ${name}. Probably caused by date ranges not being merged before calling mergeScopeDataIntoInventoryContext.`
    );

  scopeDataList
    .filter(({ scopeData }) => !scopeData.dateRangeFieldMap)
    .forEach(({ name }) => logError(createError(name)));
};

export const getEmptyInventoryApiScopeData = (): APIScopeData => ({
  ...scopeDataOperations.getEmpty(),
  dateRangeFieldMap: new Map(),
});

export const mergeScopeDataIntoInventoryContext = (
  api: GridApi,
  nextScopeData: APIScopeData
): void => {
  const context = api.getGridOption('context') as SharedInventoryContext | null;
  if (!context) {
    return;
  }
  const { apiScopeData } = context;
  const mergedApiScopeData = scopeDataOperations.merge(
    nextScopeData,
    apiScopeData
  );

  logIfScopeDataDateRangeIsNotMerged([
    { scopeData: apiScopeData, name: 'apiScopeData from context' },
    { scopeData: nextScopeData, name: 'nextScopeData' },
  ]);

  const mergedDateRangeFieldMap = new Map([
    ...(nextScopeData.dateRangeFieldMap?.entries() ?? []),
    ...(apiScopeData.dateRangeFieldMap?.entries() ?? []),
  ]);

  // Re-add dateRangeFieldMap as it's removed during the merge operation
  const mergedApiScopeDataWithDateRangeFieldMap: APIScopeData = {
    ...mergedApiScopeData,
    dateRangeFieldMap: mergedDateRangeFieldMap,
  };

  const enhancedMergedScopeData = scopeDataOperations.enhance(
    mergedApiScopeDataWithDateRangeFieldMap
  );

  api.setGridOption('context', {
    ...context,
    scopeData: enhancedMergedScopeData,
    apiScopeData: mergedApiScopeDataWithDateRangeFieldMap,
  });
};
